/**
 * 요양시설·주야간보호 월별 임금설정 정책 (단일 소스)
 * — 임금합계·비과세·대장 표시는 이 파일 기준으로 통일
 * ★ 지급 항목 합계(기본급·수당 전액)는 유지한다
 * ★ 생산직 비과세(요양보호사·조리·통상 260만 이하)는 방문요양과 같이
 *    연·야·휴를 월 20만 한도로 과세/비과세 분리한다 (회계처리용)
 * ★ 회계처리용 중식비 채우기는 「중식비 실비변상」이 켜진 행만
 *    — 만근: 31일 20만 · 30일 198,500 · 2월 197,000 (근로일수 무관)
 *    — 중도 입·퇴사: 당월 한도 × 재직일 / 월일수 비율
 *    — 꺼짐: 시설 입력 중식비만 (채우지 않음)
 * ★ 회계용 대장: 실비변상 시 중식 한도 채움 표시(기본급↔중식) + 그 과세로 공제 재산출
 *    고객용 대장·임금설정 화면은 기본급·중식 원본 유지 (채움 미적용)
 *    식비공제(l_sik)는 시설 입력 중식비 유지
 * ★ 중도입퇴 한도: 2026-08부터 1일입사·말일퇴사=만근 (그 이전 월은 레거시 유지)
 * ★ 시급×시간 기본급·연야휴 1의자리 절삭은 2026-09부터
 *    (8월 이하 임금신고 완료분 보호 — wageOnesTruncatePolicy)
 */
import {
  shouldTruncateWageOnes,
  truncateWageOnes,
  truncateWagePremiumPair,
} from './wageOnesTruncatePolicy';
import { getJobTypeSortRank } from './jobTypeSort';

/** 방문요양과 동일 — 생산직 비과세 대상 직종 */
const COMMON_WAGE_PRODUCTION_POSITIONS = ['요양보호사', '조리사', '조리원'] as const;

export const COMMON_WAGE_OVERTIME_NONTAX_MONTHLY_LIMIT = 200000;
export const COMMON_WAGE_LUNCH_MONTHLY_LIMIT = 200000;
export const COMMON_WAGE_PRODUCTION_USUAL_LIMIT = 2600000;

export function isCommonWageProductionPosition(position?: string | null): boolean {
  if (!position) return false;
  return COMMON_WAGE_PRODUCTION_POSITIONS.some((p) => position.includes(p));
}

export type CommonWagePayPartsRow = {
  cwhd_basic?: number | string | null;
  cwhd_overtime?: number | string | null;
  cwhd_overtime_nontax?: number | string | null;
  cwhd_night?: number | string | null;
  cwhd_night_nontax?: number | string | null;
  cwhd_holiday?: number | string | null;
  cwhd_holiday_nontax?: number | string | null;
  cwhd_dayoff?: number | string | null;
  cwhd_senior?: number | string | null;
  cwhd_sunim?: number | string | null;
  cwhd_bonus?: number | string | null;
  cwhd_bosu?: number | string | null;
  cwhd_bojon?: number | string | null;
  cwhd_other?: number | string | null;
  cwhd_dang?: number | string | null;
  cwhd_lunch?: number | string | null;
  cwhd_car?: number | string | null;
  cwhd_child?: number | string | null;
  cwhd_wage_total?: number | string | null;
  cwhd_taxable?: number | string | null;
  cwhd_usual?: number | string | null;
  cwhd_production_nontax?: number | string | boolean | null;
  cwhd_lunch_expense?: number | string | boolean | null;
  cwhd_work_days?: number | string | null;
  cwhd_ym?: string | number | null;
  /** 이미 저장된 임금상세 PK — 있으면 중식 중도 산식은 레거시 유지 */
  cwhd_idx?: number | string | null;
  ce_position?: string | null;
  ce_job_type?: string | null;
  ce_join_date?: string | Date | null;
  ce_leave_date?: string | Date | null;
  clci_work_start_date?: string | Date | null;
  clci_work_end_date?: string | Date | null;
  is_month_enter?: boolean | null;
  is_month_leave?: boolean | null;
};

/** DB/API 금액 → 숫자 */
export function dbCommonWageAmount(value: number | string | null | undefined): number {
  return Number(value) || 0;
}

/** 연장수당 전액 (과세 + 비과세) */
export function getCommonWageOvertimeTotal(row: CommonWagePayPartsRow): number {
  return dbCommonWageAmount(row.cwhd_overtime) + dbCommonWageAmount(row.cwhd_overtime_nontax);
}

/** 야간수당 전액 (과세 + 비과세) */
export function getCommonWageNightTotal(row: CommonWagePayPartsRow): number {
  return dbCommonWageAmount(row.cwhd_night) + dbCommonWageAmount(row.cwhd_night_nontax);
}

/** 휴일수당 전액 (과세 + 비과세) */
export function getCommonWageHolidayTotal(row: CommonWagePayPartsRow): number {
  return dbCommonWageAmount(row.cwhd_holiday) + dbCommonWageAmount(row.cwhd_holiday_nontax);
}

/**
 * 저장 직전 — 기본급·연야휴 1의자리 절삭
 * 2026-09부터. 8월 이하(임금신고 완료)는 그대로.
 */
export function applyWageOnesTruncateToPayParts<T extends CommonWagePayPartsRow>(
  row: T,
  year: number,
  month: number
): T {
  if (!shouldTruncateWageOnes(year, month)) return row;
  const oldBasic = dbCommonWageAmount(row.cwhd_basic);
  const basic = truncateWageOnes(oldBasic, year, month);
  const ot = truncateWagePremiumPair(row.cwhd_overtime, row.cwhd_overtime_nontax, year, month);
  const night = truncateWagePremiumPair(row.cwhd_night, row.cwhd_night_nontax, year, month);
  const hol = truncateWagePremiumPair(row.cwhd_holiday, row.cwhd_holiday_nontax, year, month);
  const next = {
    ...row,
    cwhd_basic: basic,
    cwhd_overtime: ot.tax,
    cwhd_overtime_nontax: ot.nontax,
    cwhd_night: night.tax,
    cwhd_night_nontax: night.nontax,
    cwhd_holiday: hol.tax,
    cwhd_holiday_nontax: hol.nontax,
  };
  if (dbCommonWageAmount(row.cwhd_usual) > 0) {
    next.cwhd_usual = dbCommonWageAmount(row.cwhd_usual) - oldBasic + basic;
  }
  next.cwhd_wage_total = getCommonWageLedgerGrossPay(next);
  next.cwhd_taxable = calcCommonWageTaxableFromParts(next);
  return next;
}

/** 식대·차량·양육·당직 비과세 합 */
export function getCommonWageFixedNontaxTotal(row: CommonWagePayPartsRow): number {
  return (
    dbCommonWageAmount(row.cwhd_dang) +
    dbCommonWageAmount(row.cwhd_lunch) +
    dbCommonWageAmount(row.cwhd_car) +
    dbCommonWageAmount(row.cwhd_child)
  );
}

/** 생산직 연·야·휴 과세 합 */
export function getCommonWagePremiumTaxTotal(row: CommonWagePayPartsRow): number {
  return (
    dbCommonWageAmount(row.cwhd_overtime) +
    dbCommonWageAmount(row.cwhd_night) +
    dbCommonWageAmount(row.cwhd_holiday)
  );
}

/** 생산직 연·야·휴 비과세 합 */
export function getCommonWagePremiumNontaxTotal(row: CommonWagePayPartsRow): number {
  return (
    dbCommonWageAmount(row.cwhd_overtime_nontax) +
    dbCommonWageAmount(row.cwhd_night_nontax) +
    dbCommonWageAmount(row.cwhd_holiday_nontax)
  );
}

/**
 * 지급 항목 합계 (표시 컬럼 기준)
 * = 기본급 + 연·야·휴(전액) + 연차·장기·선임·상여·교육·보존·기타 + 당직·식대·차량·양육(비과세)
 */
export function sumCommonWagePayParts(row: CommonWagePayPartsRow): number {
  return (
    dbCommonWageAmount(row.cwhd_basic) +
    getCommonWageOvertimeTotal(row) +
    getCommonWageNightTotal(row) +
    getCommonWageHolidayTotal(row) +
    dbCommonWageAmount(row.cwhd_dayoff) +
    dbCommonWageAmount(row.cwhd_senior) +
    dbCommonWageAmount(row.cwhd_sunim) +
    dbCommonWageAmount(row.cwhd_bonus) +
    dbCommonWageAmount(row.cwhd_bosu) +
    dbCommonWageAmount(row.cwhd_bojon) +
    dbCommonWageAmount(row.cwhd_other) +
    getCommonWageFixedNontaxTotal(row)
  );
}

/**
 * 임금대장·명세서 지급합계
 * — 항목 합을 우선 (DB cwhd_wage_total 이 0/누락이어도 표시 일치)
 */
export function getCommonWageLedgerGrossPay(row: CommonWagePayPartsRow): number {
  const parts = sumCommonWagePayParts(row);
  if (parts > 0) return parts;
  return dbCommonWageAmount(row.cwhd_wage_total);
}

/** 과세임금 = 지급합계 − (연·야·휴 비과세 + 당직·식대·차량·양육) */
export function calcCommonWageTaxableFromParts(row: CommonWagePayPartsRow): number {
  const gross = getCommonWageLedgerGrossPay(row);
  const nontax = getCommonWagePremiumNontaxTotal(row) + getCommonWageFixedNontaxTotal(row);
  return Math.max(0, gross - nontax);
}

/** 실수령액 = 지급합계 − 근로자공제합계 */
export function calcCommonWageLedgerNetPay(
  row: CommonWagePayPartsRow & { cwhd_l_total?: number | string | null },
  grossPay?: number
): number {
  const gross = grossPay != null ? grossPay : getCommonWageLedgerGrossPay(row);
  return Math.max(0, gross - dbCommonWageAmount(row.cwhd_l_total));
}

/** 금액 일치 판정 (1원 이내 허용) */
export function isCommonWageAmountEqual(a: number, b: number): boolean {
  return Math.abs(dbCommonWageAmount(a) - dbCommonWageAmount(b)) <= 1;
}

export type CommonWageLaborDeductRow = {
  cwhd_l_yeon?: number | string | null;
  cwhd_l_gun?: number | string | null;
  cwhd_l_jang?: number | string | null;
  cwhd_l_go?: number | string | null;
  cwhd_l_so?: number | string | null;
  cwhd_l_ji?: number | string | null;
  cwhd_l_jiwon?: number | string | null;
  cwhd_l_sik?: number | string | null;
  cwhd_l_etc?: number | string | null;
  cwhd_l_yearend?: number | string | null;
  cwhd_l_total?: number | string | null;
};

export type CommonWageCorpDeductRow = {
  cwhd_c_yeon?: number | string | null;
  cwhd_c_gun?: number | string | null;
  cwhd_c_jang?: number | string | null;
  cwhd_c_go?: number | string | null;
  cwhd_c_sil?: number | string | null;
  cwhd_c_san?: number | string | null;
  cwhd_c_out?: number | string | null;
  cwhd_c_total?: number | string | null;
};

/** 근로자공제 항목 합 */
export function sumCommonWageLaborDeductParts(row: CommonWageLaborDeductRow): number {
  return (
    dbCommonWageAmount(row.cwhd_l_yeon) +
    dbCommonWageAmount(row.cwhd_l_gun) +
    dbCommonWageAmount(row.cwhd_l_jang) +
    dbCommonWageAmount(row.cwhd_l_go) +
    dbCommonWageAmount(row.cwhd_l_so) +
    dbCommonWageAmount(row.cwhd_l_ji) +
    dbCommonWageAmount(row.cwhd_l_jiwon) +
    dbCommonWageAmount(row.cwhd_l_sik) +
    dbCommonWageAmount(row.cwhd_l_etc) +
    dbCommonWageAmount(row.cwhd_l_yearend)
  );
}

/** 기관공제 항목 합 */
export function sumCommonWageCorpDeductParts(row: CommonWageCorpDeductRow): number {
  return (
    dbCommonWageAmount(row.cwhd_c_yeon) +
    dbCommonWageAmount(row.cwhd_c_gun) +
    dbCommonWageAmount(row.cwhd_c_jang) +
    dbCommonWageAmount(row.cwhd_c_go) +
    dbCommonWageAmount(row.cwhd_c_sil) +
    dbCommonWageAmount(row.cwhd_c_san) +
    dbCommonWageAmount(row.cwhd_c_out)
  );
}

/** 회계처리용 — 임금합계(DB) ≠ 항목 합 */
export function isCommonWageGrossMismatch(row: CommonWagePayPartsRow): boolean {
  const parts = sumCommonWagePayParts(row);
  const stored = dbCommonWageAmount(row.cwhd_wage_total);
  if (parts === 0 && stored === 0) return false;
  return !isCommonWageAmountEqual(parts, stored);
}

/** 회계처리용 — 공제합계(DB) ≠ 근로자공제 항목 합 */
export function isCommonWageLaborTotalMismatch(row: CommonWageLaborDeductRow): boolean {
  return !isCommonWageAmountEqual(
    sumCommonWageLaborDeductParts(row),
    dbCommonWageAmount(row.cwhd_l_total)
  );
}

/** 회계처리용 — 기관합계(DB) ≠ 기관공제 항목 합 */
export function isCommonWageCorpTotalMismatch(row: CommonWageCorpDeductRow): boolean {
  return !isCommonWageAmountEqual(
    sumCommonWageCorpDeductParts(row),
    dbCommonWageAmount(row.cwhd_c_total)
  );
}

export type CommonWagePremiumNontaxSplit = {
  cwhd_overtime: number;
  cwhd_overtime_nontax: number;
  cwhd_night: number;
  cwhd_night_nontax: number;
  cwhd_holiday: number;
  cwhd_holiday_nontax: number;
};

/** 통상임금 — 저장값이 있으면 사용, 없으면 기본급+상여·교육·보존·기타 + 당직·식대·차량·양육 */
export function getCommonWageUsualWage(row: CommonWagePayPartsRow): number {
  const stored = dbCommonWageAmount(row.cwhd_usual);
  if (stored > 0) return stored;
  return (
    dbCommonWageAmount(row.cwhd_basic) +
    dbCommonWageAmount(row.cwhd_bonus) +
    dbCommonWageAmount(row.cwhd_bosu) +
    dbCommonWageAmount(row.cwhd_bojon) +
    dbCommonWageAmount(row.cwhd_other) +
    getCommonWageFixedNontaxTotal(row)
  );
}

/** 방문요양과 동일 — 해당 직종·통상 한도면 생산직 비과세 기본 ON */
export function shouldApplyCommonWageProductionNontax(
  position?: string | null,
  usualWage?: number
): boolean {
  if (!isCommonWageProductionPosition(position)) return false;
  const usual = Number(usualWage) || 0;
  return usual <= COMMON_WAGE_PRODUCTION_USUAL_LIMIT;
}

/** 연·야·휴 전액 → 과세/비과세 (월 20만, 연장→야간→휴일) */
export function splitCommonWagePremiumByProductionNontax(
  overtimeFull: number,
  nightFull: number,
  holidayFull: number,
  apply: boolean
): CommonWagePremiumNontaxSplit {
  const ot = Number(overtimeFull) || 0;
  const night = Number(nightFull) || 0;
  const hol = Number(holidayFull) || 0;
  if (!apply) {
    return {
      cwhd_overtime: ot,
      cwhd_overtime_nontax: 0,
      cwhd_night: night,
      cwhd_night_nontax: 0,
      cwhd_holiday: hol,
      cwhd_holiday_nontax: 0,
    };
  }
  let remain = COMMON_WAGE_OVERTIME_NONTAX_MONTHLY_LIMIT;
  const otNt = Math.min(ot, remain);
  remain -= otNt;
  const nightNt = Math.min(night, remain);
  remain -= nightNt;
  const holNt = Math.min(hol, remain);
  return {
    cwhd_overtime: ot - otNt,
    cwhd_overtime_nontax: otNt,
    cwhd_night: night - nightNt,
    cwhd_night_nontax: nightNt,
    cwhd_holiday: hol - holNt,
    cwhd_holiday_nontax: holNt,
  };
}

/** 시설이 입력한 중식비 */
export function getCommonWageFacilityLunch(row: CommonWagePayPartsRow): number {
  return Math.max(0, dbCommonWageAmount(row.cwhd_lunch));
}

export function isCommonWageLunchExpenseOn(row: CommonWagePayPartsRow): boolean {
  const v = row.cwhd_lunch_expense;
  return v === 1 || v === '1' || v === true || v === 'Y' || v === 'y';
}

function floorCommonWageHundred(amount: number): number {
  return Math.floor(Math.max(0, amount) / 100) * 100;
}

function parseCommonWageDateParts(
  value: string | Date | null | undefined
): { y: number; m: number; d: number } | null {
  if (value == null || value === '') return null;
  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) return null;
    return { y: value.getFullYear(), m: value.getMonth() + 1, d: value.getDate() };
  }
  const head = String(value).trim().substring(0, 10).replace(/[./]/g, '-');
  const matched = head.match(/^(\d{4})-(\d{1,2})(?:-(\d{1,2}))?/);
  if (!matched) return null;
  return {
    y: Number(matched[1]),
    m: Number(matched[2]),
    d: matched[3] ? Number(matched[3]) : 1,
  };
}

export function parseCommonWageYearMonth(row: CommonWagePayPartsRow): { year: number; month: number } | null {
  const ym = String(row.cwhd_ym ?? '').replace(/\D/g, '');
  if (ym.length < 6) return null;
  const year = Number(ym.slice(0, 4));
  const month = Number(ym.slice(4, 6));
  if (!year || month < 1 || month > 12) return null;
  return { year, month };
}

/** 만근 회계 중식 한도 — 근로일수가 아니라 그 달의 일수 */
export function getCommonWageMonthLunchLimit(year: number, month: number): number {
  if (month === 2) return 197000;
  const daysInMonth = new Date(year, month, 0).getDate();
  if (daysInMonth === 31) return 200000;
  if (daysInMonth === 30) return 198500;
  return 197000;
}

/**
 * 이미 DB에 저장된 임금상세인지
 * — 중도입퇴 저장분의 채움액 보호 판별에 사용
 */
export function hasExistingCommonWageDetail(row: CommonWagePayPartsRow): boolean {
  const idx = row.cwhd_idx;
  if (idx == null || idx === '') return false;
  const n = Number(idx);
  return Number.isFinite(n) && n > 0;
}

/**
 * 저장된 과세임금에 반영된 회계 중식비
 * — taxable = gross − (연·야·휴 비과세 + 당직 + 중식 + 차량 + 양육)
 * — 진짜 중도입퇴 행의 과거 채움액 보호용
 */
export function getImpliedAccountingLunchFromStoredTaxable(
  row: CommonWagePayPartsRow
): number | null {
  if (!hasExistingCommonWageDetail(row)) return null;
  if (!isCommonWageLunchExpenseOn(row)) return null;
  const gross = getCommonWageLedgerGrossPay(row);
  const taxable = dbCommonWageAmount(row.cwhd_taxable);
  // 과세가 없으면(미산출) 고정하지 않고 신규 산식 사용
  if (gross <= 0 || taxable <= 0) return null;
  const implied =
    gross -
    taxable -
    getCommonWagePremiumNontaxTotal(row) -
    dbCommonWageAmount(row.cwhd_car) -
    dbCommonWageAmount(row.cwhd_child);
  if (!Number.isFinite(implied) || implied < 0) return null;
  return Math.round(implied);
}

/** 중도입퇴 — 1일 입사·말일 퇴사는 만근 (신규 산식) */
function isCommonWageMidTermMonthStrict(row: CommonWagePayPartsRow): boolean {
  const ym = parseCommonWageYearMonth(row);
  const daysInMonth = ym ? new Date(ym.year, ym.month, 0).getDate() : 31;
  const join = parseCommonWageDateParts(row.ce_join_date || row.clci_work_start_date);
  const leave = parseCommonWageDateParts(row.ce_leave_date || row.clci_work_end_date);
  const joinMid =
    !!(join && ym && join.y === ym.year && join.m === ym.month && join.d > 1);
  const leaveMid =
    !!(leave && ym && leave.y === ym.year && leave.m === ym.month && leave.d < daysInMonth);
  if (joinMid || leaveMid) return true;
  if (!join && !leave && (row.is_month_enter === true || row.is_month_leave === true)) return true;
  return false;
}

export function isCommonWageMidTermMonth(row: CommonWagePayPartsRow): boolean {
  return isCommonWageMidTermMonthStrict(row);
}

/** 당월 재직 일수 — 1일 입사·말일 퇴사 = 만근 (신규 산식) */
export function getCommonWageEmployedDaysInMonth(row: CommonWagePayPartsRow): number {
  const ym = parseCommonWageYearMonth(row);
  const workDays = dbCommonWageAmount(row.cwhd_work_days);
  if (!ym) return workDays;
  const daysInMonth = new Date(ym.year, ym.month, 0).getDate();
  const join = parseCommonWageDateParts(row.ce_join_date || row.clci_work_start_date);
  const leave = parseCommonWageDateParts(row.ce_leave_date || row.clci_work_end_date);
  const start = join && join.y === ym.year && join.m === ym.month ? join.d : 1;
  const end = leave && leave.y === ym.year && leave.m === ym.month ? leave.d : daysInMonth;
  const employed = Math.max(0, Math.min(daysInMonth, end) - Math.max(1, start) + 1);

  if (employed >= daysInMonth) return daysInMonth;
  if (employed > 0 && employed < daysInMonth) return employed;
  if (isCommonWageMidTermMonthStrict(row) && workDays > 0) return Math.min(workDays, daysInMonth);
  return daysInMonth;
}

/**
 * 회계 중식 채움 한도
 * — 실비변상 OFF: 0
 * — 만근: 31일 20만 · 30일 198,500 · 2월 197,000
 * — 중도 입·퇴사: 당월 한도 × 재직일 / 월일수 (100원 단위)
 * — 기존 저장분 보호: **신산식상 진짜 중도입퇴**인 행만 DB 과세 역산 중식액 유지
 *   (만근인데 예전에 잘못 깎였거나, 채움이 안 들어간 저장분은 재산출)
 * @param ymOverride row.cwhd_ym 이 비어 있을 때 화면 선택 연·월로 한도 산출
 */
export function getCommonWageLunchFillLimit(
  row: CommonWagePayPartsRow,
  ymOverride?: { year: number; month: number } | null
): number {
  if (!isCommonWageLunchExpenseOn(row)) return 0;

  const parsed = parseCommonWageYearMonth(row);
  const year = ymOverride?.year || parsed?.year || 0;
  const month = ymOverride?.month || parsed?.month || 0;
  if (!year || !month) {
    // 연월을 모르면 일수별 한도를 못 맞춤 — 호출측에서 ym을 넣도록 함
    return 0;
  }
  const monthLimit = getCommonWageMonthLunchLimit(year, month);
  const rowForMid = {
    ...row,
    cwhd_ym: `${year}${String(month).padStart(2, '0')}`,
  };

  let computed = monthLimit;
  if (isCommonWageMidTermMonth(rowForMid)) {
    const daysInMonth = new Date(year, month, 0).getDate();
    const employed = getCommonWageEmployedDaysInMonth(rowForMid);
    if (employed <= 0) computed = 0;
    else if (employed >= daysInMonth) computed = monthLimit;
    else computed = floorCommonWageHundred((monthLimit * employed) / daysInMonth);
  }

  // 진짜 중도입퇴로 저장된 채움액만 보호 (시설별 하드코딩 없음)
  if (hasExistingCommonWageDetail(row) && isCommonWageMidTermMonth(rowForMid)) {
    const implied = getImpliedAccountingLunchFromStoredTaxable(row);
    if (implied != null && implied > 0 && implied < computed) return implied;
  }

  return computed;
}

/**
 * 추가중식비 — 실비변상 ON일 때만 한도까지 채움
 * = min(한도 − 시설입력, 기본급)
 */
export function getCommonWageAdditionalLunch(
  row: CommonWagePayPartsRow,
  ymOverride?: { year: number; month: number } | null
): number {
  const limit = getCommonWageLunchFillLimit(row, ymOverride);
  if (limit <= 0) return 0;
  const facility = Math.min(getCommonWageFacilityLunch(row), limit);
  const basic = dbCommonWageAmount(row.cwhd_basic);
  return Math.min(Math.max(0, limit - facility), basic);
}

/** 관리자·회계 중식비 = 시설입력 + 추가중식비 */
export function getCommonWageAccountingLunch(
  row: CommonWagePayPartsRow,
  ymOverride?: { year: number; month: number } | null
): number {
  return getCommonWageFacilityLunch(row) + getCommonWageAdditionalLunch(row, ymOverride);
}

/**
 * 회계처리용 — 중식비 한도 채우기 (지급 합계 유지)
 * 기본급에서 추가중식비를 빼 중식비 칸을 채운다. 시설 입력값(DB)은 바꾸지 않는다.
 */
export function applyCommonWageAccountingLunchFill<T extends CommonWagePayPartsRow>(
  row: T,
  ymOverride?: { year: number; month: number } | null
): T {
  const ym = ymOverride || parseCommonWageYearMonth(row);
  const rowWithYm: T =
    ym != null
      ? { ...row, cwhd_ym: `${ym.year}${String(ym.month).padStart(2, '0')}` }
      : row;
  const additional = getCommonWageAdditionalLunch(rowWithYm, ym);
  if (additional <= 0) return rowWithYm;
  const next = {
    ...rowWithYm,
    cwhd_basic: dbCommonWageAmount(rowWithYm.cwhd_basic) - additional,
    cwhd_lunch: getCommonWageFacilityLunch(rowWithYm) + additional,
  };
  next.cwhd_wage_total = getCommonWageLedgerGrossPay(next);
  next.cwhd_taxable = calcCommonWageTaxableFromParts(next);
  return next;
}

/** 회계처리용 표시 — 생산직 비과세 + 실비변상 시 중식비 한도 채우기 */
export function applyCommonWageAccountingDisplay<T extends CommonWagePayPartsRow>(
  row: T,
  ymOverride?: { year: number; month: number } | null
): T {
  return applyCommonWageAccountingLunchFill(applyCommonWageAccountingNontax(row), ymOverride);
}

/** 회계처리용 — 생산직 비과세를 적용한 지급 칸 (지급 합계는 유지) */
export function applyCommonWageAccountingNontax<T extends CommonWagePayPartsRow>(row: T): T {
  const apply = shouldApplyCommonWageProductionNontax(
    row.ce_position || row.ce_job_type,
    getCommonWageUsualWage(row)
  );
  const split = splitCommonWagePremiumByProductionNontax(
    getCommonWageOvertimeTotal(row),
    getCommonWageNightTotal(row),
    getCommonWageHolidayTotal(row),
    apply
  );
  const next = {
    ...row,
    ...split,
    cwhd_production_nontax: apply ? 1 : 0,
  };
  next.cwhd_wage_total = getCommonWageLedgerGrossPay(next);
  next.cwhd_taxable = calcCommonWageTaxableFromParts(next);
  return next;
}

/** 회계처리용 — 과세(DB) ≠ 항목 기준 과세 */
export function isCommonWageTaxableMismatch(row: CommonWagePayPartsRow): boolean {
  const expected = calcCommonWageTaxableFromParts(row);
  const stored = dbCommonWageAmount(row.cwhd_taxable);
  if (expected === 0 && stored === 0) return false;
  return !isCommonWageAmountEqual(expected, stored);
}

/** 기관부담 보험 = 기관공제 항목 − 퇴직금 (목록 「기관부담보험」) */
export function getCommonWageLedgerEmployerInsurance(row: CommonWageCorpDeductRow): number {
  const insurance =
    dbCommonWageAmount(row.cwhd_c_yeon) +
    dbCommonWageAmount(row.cwhd_c_gun) +
    dbCommonWageAmount(row.cwhd_c_jang) +
    dbCommonWageAmount(row.cwhd_c_go) +
    dbCommonWageAmount(row.cwhd_c_sil) +
    dbCommonWageAmount(row.cwhd_c_san);
  if (insurance > 0) return insurance;
  return Math.max(0, dbCommonWageAmount(row.cwhd_c_total) - dbCommonWageAmount(row.cwhd_c_out));
}

/** 퇴직적립금 — 저장된 금액 */
export function getCommonWageLedgerRetirement(row: CommonWageCorpDeductRow): number {
  return dbCommonWageAmount(row.cwhd_c_out);
}

function retirementAmountLocked(raw: unknown): boolean {
  if (raw == null || raw === '') return false;
  let parsed: unknown = raw;
  for (let i = 0; i < 2 && typeof parsed === 'string'; i++) {
    try {
      parsed = JSON.parse(parsed);
    } catch {
      return false;
    }
  }
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return false;
  const flag = (parsed as Record<string, unknown>).c_out;
  return flag === true || flag === 1 || flag === '1' || flag === 'Y' || flag === 'y' || flag === 'true';
}

/**
 * 열린 달 퇴직금.
 * 세율을 다시 불러오는 중에도 저장 퇴직금(화면 중식비 차감)으로 되돌아가지 않는다.
 * 실비변상이면 한도까지 채운 중식비를 빼고 12로 나눈다.
 */
export function previewCommonWageRetirement(
  row: CommonWagePayPartsRow & {
    cwhd_is_owner?: number | null;
    cwhd_manual_flags?: unknown;
    cwhd_c_out?: number | string | null;
  },
  year: number,
  month: number
): number {
  if (Number(row.cwhd_is_owner) === 1) return 0;
  if (retirementAmountLocked(row.cwhd_manual_flags)) return dbCommonWageAmount(row.cwhd_c_out);
  const rowWithYm = {
    ...row,
    cwhd_ym: `${year}${String(month).padStart(2, '0')}`,
  };
  const gross = getCommonWageLedgerGrossPay(rowWithYm);
  let base = gross;
  if (isCommonWageLunchExpenseOn(rowWithYm)) {
    base -= getCommonWageAccountingLunch(rowWithYm, { year, month });
  }
  return Math.floor(Math.round(Math.max(0, base) / 12) / 10) * 10;
}

/** 비과세 합계 (연·야·휴 비과세 + 당직·식대·차량·양육) */
export function getCommonWageLedgerNontaxTotal(row: CommonWagePayPartsRow): number {
  return getCommonWagePremiumNontaxTotal(row) + getCommonWageFixedNontaxTotal(row);
}

/** 인건비 절감 기준액 — 비과세합계 − 차량 − 양육 */
export function getCommonWageCenterSaving(row: CommonWagePayPartsRow): number {
  return Math.max(
    0,
    getCommonWageLedgerNontaxTotal(row) -
      dbCommonWageAmount(row.cwhd_car) -
      dbCommonWageAmount(row.cwhd_child)
  );
}

/** 인건비지출내역 — 종사자구분 정렬 (시설·주간, jobTypeSort 와 동일) */
export function getCommonLaborCostJobCategoryOrder(category: string): number {
  return getJobTypeSortRank(category);
}

/** 기관용 대장 — 비과세 고정수당 라벨 */
export const COMMON_WAGE_CLIENT_NONTAX_LABELS = {
  dang: '당직수당(비과세)',
  lunch: '중식비(비과세)',
  car: '차량보조비(비과세)',
  child: '양육비(비과세)',
} as const;
